import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { unit as unitEnum } from '../../../../../enum/project';
import { convertFieldValue } from '../../../../../services/UnitConversionService';
import { ensureFontsRegistered } from '../../../shared/PdfFontRegistry';
import { isEmptyUnit } from '../../shared/pdfUnitMerge';
import * as symbols from '../../../../../enum/symbols';

// Funzione di parsing base per <br> e <strong>
function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: React.ReactElement[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) result.push(
            <Text
                key={key++}
                style={{ fontFamily: 'DejaVuSans', fontWeight: 'bold', ...style }}
            >
                {boldText}
            </Text>
        );
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}

interface PavedResultsCoreProps {
    output: any;
    projectData: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    tireImageGeogrid?: string;
    chartImage?: string;
    trafficData?: {
        unstabilized?: any;
        increasedPassages?: any;
        optimized?: any;
    };
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    t: TFunction;
    showInputData?: boolean;
    showChart?: boolean;
    showResults?: boolean;
    customTitle?: string;
}

export const PavedResultsCore: React.FC<PavedResultsCoreProps> = ({
    output,
    projectData,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeogrid,
    chartImage,
    trafficData,
    surfaceDesignImages,
    baseDesignImages,
    subbaseDesignImages,
    t,
    showInputData = true,
    showChart = true,
    showResults = true,
    customTitle,
}) => {
    ensureFontsRegistered();
    const aashtoResults = output;
    const unitSystem = projectData?.unit === 'Imperial' ? unitEnum.Imperial : unitEnum.SI;
    const projectInputs = projectData?.projectInfo?.data;

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === unitEnum.SI ? metricUnit : imperialUnit;
    };

    // Helper for consistent conversion and rounding for input tables
    // FIX: Always convert from Metric to Imperial when in Imperial mode
    // Data in projectData.projectInfo.data is ALWAYS stored in Metric from the backend
    // The conversionApplied flag tracks form display state, NOT the data storage format
    const convertInputValue = (value: number | undefined, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        if (typeof value !== 'number') return 'N/A';

        // Always convert when Imperial mode - backend data is always Metric
        if (unitSystem === unitEnum.Imperial) {
            const converted = convertFieldValue({ name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true }, value, unitEnum.SI, unitEnum.Imperial, `pdf.${fieldName}`);
            return converted !== null ? converted.toFixed(3) : value.toString();
        }

        // Metric mode - no conversion needed
        return value.toFixed(3);
    };

    const methodData = projectData?.projectInfo?.data as any;

    // Helper function to render traffic analysis table - simplified to match Results page
    const renderTrafficTable = (title: string, data: any, backgroundColor = '#f9f9f9') => (
        <View style={{ width: '100%', marginTop: 8, padding: 8, border: '1 solid #ddd', borderRadius: 4, backgroundColor }}>
            <Text style={{ fontSize: 10, fontWeight: 'bold', textAlign: 'center', marginBottom: 6 }}>{title}</Text>
            <View style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Traffic Analysis - show ESAL */}
                {data?.totalEsal !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1 }}>
                        <Text style={{ fontSize: 9 }}>ESAL:</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold' }}>{data.totalEsal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                    </View>
                )}
                {/* Traffic Benefit - show TBR */}
                {data?.tbr !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1 }}>
                        <Text style={{ fontSize: 9 }}>TBR:</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#2e7d32' }}>{data.tbr.toFixed(1)}</Text>
                    </View>
                )}
                {/* Optimized Design - show Thickness Saving */}
                {data?.thicknessSaving !== undefined && (
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1 }}>
                        <Text style={{ fontSize: 9 }}>Thickness Saving:</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#ff6f00' }}>{data.thicknessSaving.toFixed(1)}%</Text>
                    </View>
                )}
            </View>
        </View>
    );

    // Helper function to render design section table (Surface/Base/Subbase) - simplified to match Results page
    const renderDesignSectionTable = (tableData: any, tableType: 'unstabilized' | 'increased' | 'optimized', backgroundColor: string) => {
        if (!tableData) return null;

        return (
            <View style={{ width: '100%', marginTop: 6, padding: 6, border: '0.5px solid #ddd', borderRadius: 2, backgroundColor }}>
                {tableType === 'unstabilized' && tableData.totalEsal !== undefined && (
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: 7 }}>ESAL:</Text>
                        <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{tableData.totalEsal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                    </View>
                )}
                {tableType === 'increased' && tableData.tbr !== undefined && (
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: 7 }}>TBR:</Text>
                        <Text style={{ fontSize: 7, fontWeight: 'bold', color: '#2e7d32' }}>{tableData.tbr.toFixed(1)}</Text>
                    </View>
                )}
                {tableType === 'optimized' && tableData.thicknessSaving !== undefined && (
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: 7 }}>Thickness Saving:</Text>
                        <Text style={{ fontSize: 7, fontWeight: 'bold', color: '#ff6f00' }}>{tableData.thicknessSaving.toFixed(1)}%</Text>
                    </View>
                )}
            </View>
        );
    };

    // Determine if there is any paved input data to display
    const hasAnyInputData = methodData && (
        methodData.roadInfo ||
        methodData.trafficLoad ||
        methodData.surface ||
        methodData.baseCourse ||
        methodData.subbaseCourse ||
        methodData.subgrade
    );

    return (
        <>
            {/* Title */}
            {customTitle && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title]}>{customTitle}</Text>
                    <View style={pdfSharedStyles.spacer1} />
                </>
            )}

            {!customTitle && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <Text>{parseHtmlToPdfText(t('paved_project_info_desc_pdf') || 'Project Information', pdfSharedStyles.projectInfoSubtitle)}</Text>
                </>
            )}

            {/* Results Section */}
            {showResults && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>
                    {/* <View style={pdfSharedStyles.spacer0} /> */}

                    {/* Paved Inputs Tables Section - BEFORE canvas (separate tables per accordion) */}
                    {showInputData && hasAnyInputData && (
                        <>
                            
                            <Text style={[pdfSharedStyles.title, { marginTop: 8, marginBottom: 0, textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                            <View style={pdfSharedStyles.spacer0} />

                            {/* Row 1: Road Information & Traffic Load */}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }} wrap={false}>
                                <View style={{ width: '48%' }}>
                                    {methodData?.roadInfo && (
                                        <PdfTable title={t('road_information') as string} t={t}>
                                            <PdfRow
                                                label={t('carriageway_width') as string}
                                                symbol="W"
                                                value={convertInputValue(methodData.roadInfo.carriagewayWidth, 'carriagewayWidth', 'm', 'ft')}
                                                unit={getUnit('m', 'ft')}
                                            />
                                            <PdfRow
                                                label={t('longitudinal_slope') as string}
                                                symbol="S"
                                                value={convertInputValue(methodData.roadInfo.longitudinalSlope, 'longitudinalSlope', '%', '%')}
                                                unit={getUnit('%', '%')}
                                            />
                                            <PdfRow
                                                label={t('transversal_slope') as string}
                                                symbol={symbols.sym_sx}
                                                value={convertInputValue(methodData.roadInfo.transversalSlope, 'transversalSlope', '%', '%')}
                                                unit={getUnit('%', '%')}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                                <View style={{ width: '48%' }}>
                                    {methodData?.trafficLoad && (
                                        <PdfTable title={t('traffic_load') as string} t={t}>
                                            <PdfRow
                                                label={t('functional_classification') as string}
                                                value={methodData.trafficLoad.functionalClassification}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.functionalClassification}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('road_type') as string}
                                                value={methodData.trafficLoad.roadType}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.roadType}
                                            />
                                            <PdfRow
                                                label={t('reliability_level') as string}
                                                symbol="R"
                                                value={methodData.trafficLoad.reliabilityLevel?.toString()}
                                                unit="%"
                                                condition={!!methodData.trafficLoad.reliabilityLevel}
                                            />
                                            <PdfRow
                                                label={t('combined_standard_error') as string}
                                                symbol={symbols.sym_so}
                                                value={convertInputValue(methodData.trafficLoad.combinedStandardError, 'combinedStandardError', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.combinedStandardError}
                                            />
                                            <PdfRow
                                                label={t('initial_service_index') as string}
                                                symbol={symbols.sym_po}
                                                value={convertInputValue(methodData.trafficLoad.initialServiceIndex, 'initialServiceIndex', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.initialServiceIndex}
                                            />
                                            <PdfRow
                                                label={t('terminal_surface_index') as string}
                                                symbol={symbols.sym_pt}
                                                value={convertInputValue(methodData.trafficLoad.terminalSurfaceIndex, 'terminalSurfaceIndex', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.terminalSurfaceIndex}
                                            />
                                            <PdfRow
                                                label={t('esal_calculation_mode') as string}
                                                value={methodData.trafficLoad.esalCalculationMode}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.esalCalculationMode}
                                            />
                                            <PdfRow
                                                label={t('esal_final_value') as string}
                                                symbol="ESAL"
                                                value={convertInputValue(methodData.trafficLoad.esalFinalValue, 'esalFinalValue', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.trafficLoad.esalFinalValue}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                            </View>

                            {/* Page break after Traffic Load */}
                            <View break />

                            {/* Row 2: Surface & Base Course */}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }} wrap={false}>
                                <View style={{ width: '48%' }}>
                                    {methodData?.surface && (
                                        <PdfTable title={t('surface') as string} t={t}>
                                            <PdfRow
                                                label={t('surface_material') as string}
                                                value={t(methodData.surface.surfaceMaterial) as string}
                                                unit="-"
                                                condition={!!methodData.surface.surfaceMaterial}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('minimum_thickness_of_surface_layer') as string}
                                                symbol={symbols.sym_d1}
                                                value={convertInputValue(methodData.surface.minThickness, 'minThickness', 'm', 'ft')}
                                                unit={getUnit('m', 'ft')}
                                                condition={methodData.surface.minThickness !== undefined}
                                            />
                                            <PdfRow
                                                label={t('surface_layer_coefficient') as string}
                                                symbol={symbols.sym_a1}
                                                value={convertInputValue(methodData.surface.layerCoefficienta1, 'layerCoefficienta1', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.surface.layerCoefficienta1}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                                <View style={{ width: '48%' }}>
                                    {methodData?.baseCourse && (
                                        <PdfTable title={t('base_course') as string} t={t}>
                                            <PdfRow
                                                label={t('base_material') as string}
                                                value={t(methodData.baseCourse.baseMaterial) as string}
                                                unit="-"
                                                condition={!!methodData.baseCourse.baseMaterial}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('minimum_thickness_of_base_layer') as string}
                                                symbol={symbols.sym_d2}
                                                value={convertInputValue(methodData.baseCourse.minThickness, 'minThickness', 'm', 'ft')}
                                                unit={getUnit('m', 'ft')}
                                                condition={methodData.baseCourse.minThickness !== undefined}
                                            />
                                            <PdfRow
                                                label={t('base_layer_coefficient') as string}
                                                symbol={symbols.sym_a2}
                                                value={convertInputValue(methodData.baseCourse.layerCoefficienta2, 'layerCoefficienta2', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.baseCourse.layerCoefficienta2}
                                            />
                                            <PdfRow
                                                label={t('select_geogrid_for_base_course') as string}
                                                value={t(methodData.baseCourse.baseGeogrid) as string || '-'}
                                                unit="-"
                                                condition={!!methodData.baseCourse.baseGeogrid && methodData.baseCourse.baseGeogrid.trim() !== ''}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('cbr_base_course') as string}
                                                symbol="CBR"
                                                value={convertInputValue(methodData.baseCourse.cbrBaseCourse, 'cbrBaseCourse', '%', '%')}
                                                unit={getUnit('%', '%')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'cbr' && !!methodData.baseCourse.cbrBaseCourse}
                                            />
                                            <PdfRow
                                                label={t('ev2_base_course') as string}
                                                symbol={symbols.sym_ev2}
                                                value={convertInputValue(methodData.baseCourse.ev2BaseCourse, 'ev2BaseCourse', 'MPa', 'psi')}
                                                unit={getUnit('MPa', 'psi')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'ev2' && !!methodData.baseCourse.ev2BaseCourse}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                            </View>

                            {/* Row 3: Subbase Course & Subgrade */}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }} wrap={false}>
                                <View style={{ width: '48%' }}>
                                    {methodData?.subbaseCourse && (
                                        <PdfTable title={t('subbase_course') as string} t={t}>
                                            <PdfRow
                                                label={t('subbase_material') as string}
                                                value={t(methodData.subbaseCourse.subbaseMaterial) as string}
                                                unit="-"
                                                condition={!!methodData.subbaseCourse.subbaseMaterial}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('minimum_thickness_of_subbase_layer') as string}
                                                symbol={symbols.sym_d3}
                                                value={convertInputValue(methodData.subbaseCourse.minThickness, 'minThickness', 'm', 'ft')}
                                                unit={getUnit('m', 'ft')}
                                                condition={methodData.subbaseCourse.minThickness !== undefined}
                                            />
                                            <PdfRow
                                                label={t('subbase_layer_coefficient') as string}
                                                symbol={symbols.sym_a3}
                                                value={convertInputValue(methodData.subbaseCourse.layerCoefficienta3, 'layerCoefficienta3', '-', '-')}
                                                unit="-"
                                                condition={!!methodData.subbaseCourse.layerCoefficienta3}
                                            />
                                            <PdfRow
                                                label={t('select_geogrid_for_subbase_course') as string}
                                                value={t(methodData.subbaseCourse.subbaseGeogrid) as string || '-'}
                                                unit="-"
                                                condition={!!methodData.subbaseCourse.subbaseGeogrid && methodData.subbaseCourse.subbaseGeogrid.trim() !== ''}
                                                colSpan={3}
                                            />
                                            <PdfRow
                                                label={t('cbr_subbase_course') as string}
                                                symbol="CBR"
                                                value={convertInputValue(methodData.subbaseCourse.cbrSubbaseCourse, 'cbrSubbaseCourse', '%', '%')}
                                                unit={getUnit('%', '%')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'cbr' && !!methodData.subbaseCourse.cbrSubbaseCourse}
                                            />
                                            <PdfRow
                                                label={t('ev2_subbase_course') as string}
                                                symbol={symbols.sym_ev2}
                                                value={convertInputValue(methodData.subbaseCourse.ev2SubbaseCourse, 'ev2SubbaseCourse', 'MPa', 'psi')}
                                                unit={getUnit('MPa', 'psi')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'ev2' && !!methodData.subbaseCourse.ev2SubbaseCourse}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                                <View style={{ width: '48%' }}>
                                    {methodData?.subgrade && (
                                        <PdfTable title={t('subgrade') as string} t={t}>
                                            <PdfRow
                                                label={t('cbr_subgrade') as string}
                                                symbol="CBR"
                                                value={convertInputValue(methodData.subgrade.cbrSubgrade, 'cbrSubgrade', '%', '%')}
                                                unit={getUnit('%', '%')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'cbr' && !!methodData.subgrade.cbrSubgrade}
                                            />
                                            <PdfRow
                                                label={t('ev2_subgrade') as string}
                                                symbol={symbols.sym_ev2}
                                                value={convertInputValue(methodData.subgrade.ev2Subgrade, 'ev2Subgrade', 'MPa', 'psi')}
                                                unit={getUnit('MPa', 'psi')}
                                                condition={methodData.roadInfo?.subgradeInputMethod === 'ev2' && !!methodData.subgrade.ev2Subgrade}
                                            />
                                        </PdfTable>
                                    )}
                                </View>
                            </View>

                            {/* Chart for structural analysis */}
                            {showChart && chartImage && (
                                <View style={pdfSharedStyles.graphicSection} wrap={false}>
                                    <View style={pdfSharedStyles.spacer2} />
                                    <Text style={pdfSharedStyles.subTitle}>{t('structural_analysis')}</Text>
                                    <Image style={pdfSharedStyles.chartImage} src={chartImage} />
                                </View>
                            )}
                        </>
                    )}

                    {/* Tire Images */}
                    {(tireImageUnreinforced || tireImageReinforced) && (
                        <View style={[pdfSharedStyles.graphicImagesContainer, {}]}>
                            <View style={[{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }]}>
                                {tireImageUnreinforced ? (
                                    <Image key={tireImageUnreinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageUnreinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unreinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilised', 'Unstabilised')}</Text>
                                {trafficData?.unstabilized && renderTrafficTable(t('traffic_analysis'), trafficData.unstabilized, '#f9f9f9')}
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                {tireImageReinforced ? (
                                    <Image key={tireImageReinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageReinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Reinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('traffic_increase') || 'Traffic Increase'}</Text>
                                {trafficData?.increasedPassages && renderTrafficTable(t('traffic_benefit'), trafficData.increasedPassages, '#e8f5e8')}
                            </View>
                            <View style={{ alignItems: 'center', justifyContent: 'flex-end', width: '33.33%', height: '300' }}>
                                <Image key={tireImageGeogrid?.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageGeogrid} />
                                <Text style={pdfSharedStyles.graphicDescription}>{t('geogrid_stabilized') || 'GEOGRID Stabilized'}</Text>
                                {trafficData?.optimized && renderTrafficTable(t('optimized_design'), trafficData.optimized, '#fff3e0')}
                            </View>
                        </View>
                    )}

                    {/* Dynamic Design Sections */}
                    {(surfaceDesignImages || baseDesignImages || subbaseDesignImages) && (
                        <>
                            <View break />
                            <View style={pdfSharedStyles.spacer2} />
                            
                        </>
                    )}

                    {/* Surface Design Section */}
                    {surfaceDesignImages && (surfaceDesignImages.canvas1 || surfaceDesignImages.canvas2 || surfaceDesignImages.canvas3) && (
                        <View style={pdfSharedStyles.section}>
                            <Text style={[pdfSharedStyles.subTitle, { marginBottom: 8 }]}>{t('surface_design') || 'Surface Design'}</Text>
                            <View style={[pdfSharedStyles.graphicImagesContainer, { justifyContent: 'space-around' }]}>
                                {surfaceDesignImages.canvas1 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={surfaceDesignImages.canvas1} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('unstabilized') || 'Unstabilized'}</Text>
                                        {renderDesignSectionTable(trafficData?.unstabilized, 'unstabilized', '#f9f9f9')}
                                    </View>
                                )}
                                {surfaceDesignImages.canvas2 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={surfaceDesignImages.canvas2} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('traffic_increase') || 'Traffic Increase'}</Text>
                                        {renderDesignSectionTable(surfaceDesignImages.tableData2, 'increased', '#e8f5e8')}
                                    </View>
                                )}
                                {surfaceDesignImages.canvas3 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={surfaceDesignImages.canvas3} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('optimized_with_geogrid') || 'Optimized with Geogrid'}</Text>
                                        {renderDesignSectionTable(surfaceDesignImages.tableData3, 'optimized', '#fff3e0')}
                                    </View>
                                )}
                            </View>
                            <View style={pdfSharedStyles.spacer2} />
                        </View>
                    )}

                    {/* Base Design Section */}
                    {baseDesignImages && (baseDesignImages.canvas1 || baseDesignImages.canvas2 || baseDesignImages.canvas3) && (
                        <View style={pdfSharedStyles.section}>
                            <Text style={[pdfSharedStyles.subTitle, { marginBottom: 8 }]}>{t('base_design') || 'Base Design'}</Text>
                            <View style={[pdfSharedStyles.graphicImagesContainer, { justifyContent: 'space-around' }]}>
                                {baseDesignImages.canvas1 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={baseDesignImages.canvas1} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('unstabilized') || 'Unstabilized'}</Text>
                                        {renderDesignSectionTable(trafficData?.unstabilized, 'unstabilized', '#f9f9f9')}
                                    </View>
                                )}
                                {baseDesignImages.canvas2 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={baseDesignImages.canvas2} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('traffic_increase') || 'Traffic Increase'}</Text>
                                        {renderDesignSectionTable(baseDesignImages.tableData2, 'increased', '#e8f5e8')}
                                    </View>
                                )}
                                {baseDesignImages.canvas3 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={baseDesignImages.canvas3} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('optimized_with_geogrid') || 'Optimized with Geogrid'}</Text>
                                        {renderDesignSectionTable(baseDesignImages.tableData3, 'optimized', '#fff3e0')}
                                    </View>
                                )}
                            </View>
                            <View style={pdfSharedStyles.spacer2} />
                        </View>
                    )}

                    {/* Subbase Design Section */}
                    {subbaseDesignImages && (subbaseDesignImages.canvas1 || subbaseDesignImages.canvas2 || subbaseDesignImages.canvas3) && (
                        <View style={pdfSharedStyles.section}>
                            <Text style={[pdfSharedStyles.subTitle, { marginBottom: 8 }]}>{t('subbase_design') || 'Subbase Design'}</Text>
                            <View style={[pdfSharedStyles.graphicImagesContainer, { justifyContent: 'space-around' }]}>
                                {subbaseDesignImages.canvas1 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={subbaseDesignImages.canvas1} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('unstabilized') || 'Unstabilized'}</Text>
                                        {renderDesignSectionTable(trafficData?.unstabilized, 'unstabilized', '#f9f9f9')}
                                    </View>
                                )}
                                {subbaseDesignImages.canvas2 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={subbaseDesignImages.canvas2} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('traffic_increase') || 'Traffic Increase'}</Text>
                                        {renderDesignSectionTable(subbaseDesignImages.tableData2, 'increased', '#e8f5e8')}
                                    </View>
                                )}
                                {subbaseDesignImages.canvas3 && (
                                    <View style={{ width: '32%', alignItems: 'center' }}>
                                        <Image style={[pdfSharedStyles.graphicImage, { width: '100%', height: 'auto', maxHeight: 200 }]} src={subbaseDesignImages.canvas3} />
                                        <Text style={[pdfSharedStyles.graphicDescription, { fontSize: 8, marginTop: 4 }]}>{t('optimized_with_geogrid') || 'Optimized with Geogrid'}</Text>
                                        {renderDesignSectionTable(subbaseDesignImages.tableData3, 'optimized', '#fff3e0')}
                                    </View>
                                )}
                            </View>
                            <View style={pdfSharedStyles.spacer2} />
                        </View>
                    )}

                    <View style={pdfSharedStyles.spacer3} />
                </>
            )}
        </>
    );
};

interface PdfRowProps {
    label: string;
    symbol?: string | React.ReactElement;
    value?: string;
    unit?: string;
    colSpan?: number;
    condition?: boolean;
    isEven?: boolean;
}

const PdfRow: React.FC<PdfRowProps> = ({ label, symbol = '', value = '', unit = '', colSpan = 1, condition = true, isEven = false }: PdfRowProps) => {
    if (condition === false) return null;

    // PM UX 2026-04-19: auto-merge Value+Unit when unit is absent (dash/empty/undefined)
    const effectiveColSpan = colSpan === 1 && typeof unit === 'string' && isEmptyUnit(unit) ? 2 : colSpan;

    const rowStyle = [pdfSharedStyles.tableRow, isEven ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow];

    let valueWidth = '20%';
    let showSymbol = true;
    let showUnit = true;

    if (effectiveColSpan === 3) {
        valueWidth = '65%'; // 25 (Symbol) + 20 (Value) + 20 (Unit)
        showSymbol = false;
        showUnit = false;
    } else if (effectiveColSpan === 2) {
        valueWidth = '40%'; // 20 (Value) + 20 (Unit)
        showUnit = false;
    }

    return (
        <View style={rowStyle}>
            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
            </View>

            {showSymbol && (
                <View style={pdfSharedStyles.tableColSymbol}>
                    <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                </View>
            )}

            <View style={[pdfSharedStyles.tableColValue, { width: valueWidth }]}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>

            {showUnit && (
                <View style={pdfSharedStyles.tableColUnit}>
                    <Text style={pdfSharedStyles.tableCell}>{unit}</Text>
                </View>
            )}
        </View>
    );
};

interface PdfTableProps {
    title: string;
    children: React.ReactNode;
    t: TFunction;
}

const PdfTable: React.FC<PdfTableProps> = ({ title, children, t }: PdfTableProps) => {
    const validChildren = React.Children.toArray(children).filter((child: React.ReactNode): child is React.ReactElement => React.isValidElement(child));
    if (validChildren.length === 0) return null;

    return (
        <View style={[pdfSharedStyles.section, { width: '100%' }]}> 
            <View style={pdfSharedStyles.spacer1} />
            <Text style={pdfSharedStyles.tableTitle}>{title}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <View style={pdfSharedStyles.table}>
                <View style={pdfSharedStyles.tableRow}>
                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderSymbol}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderValue}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderUnit}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                    </View>
                </View>
                {validChildren.map((child: React.ReactElement, idx: number) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<PdfRowProps>, { isEven: idx % 2 === 0 });
                    }
                    return child;
                })}
            </View>
        </View>
    );
}
